import { HOOK_CANVAS__from_image } from '@/pages/page_generation/layouts/_base/HOOK_CANVAS__from_image';
import { assert_equal, assert_not_null } from '@shared/asserts/utils_asserts';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { useEffect, useRef } from 'react';

interface PROPS {
  field: FIELD__image_asset;
}

export function G_IMAGE_OVERLAY__segments_disabled_to_opacity({ field }: PROPS) {
  const ref_canvas = useRef<HTMLCanvasElement | null>(null);

  // Filter out only the segments that are currently marked as disabled
  const segments_disabled = field.get_segments_disabled();

  let canvas_image = HOOK_CANVAS__from_image(field.asset);

  /*
   * EFFECTS
   */

  useEffect(() => {
    if (canvas_image.is_zero_size()) return;

    const canvas = ref_canvas.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    assert_not_null(ctx);

    // Grab the first available segment or fallback to field dimensions to get total pixels
    const segments_all = field.asset.segments_map.values();
    if (segments_all.length === 0) return;

    const segment_first = segments_all[0];
    const pixels_total = segment_first.pixels_total;

    // Use the dimensions from the main image asset / mask
    [canvas.width, canvas.height] = [segment_first.mask_width, segment_first.mask_height];

    // 1. Draw a solid white background first (per requirement: canvas should be non-transparent)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 2. Scale and draw the source canvas_image onto our target canvas
    // canvas_image is an instance of HTMLCanvasElement or similar renderable image source
    ctx.drawImage(canvas_image, 0, 0, canvas.width, canvas.height);

    // 3. Extract the actual pixel buffer containing the scaled image pixels
    const image_data = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // 4. Continuous accumulation mask (0 = allowed/enabled, 1 = disabled)
    let mask_combined = new Uint8Array(pixels_total);

    // 5. Decode each disabled segment and burn it into our combined array
    for (const segment of segments_disabled) {
      assert_equal(segment.pixels_total, pixels_total);
      segment.accumulate_to_mask(mask_combined);
    }

    // 6. Apply opacities directly to the existing image pixels based on mask status
    for (let i = 0; i < pixels_total; i++) {
      const offset = i * 4;

      if (mask_combined[i] === 1) {
        // Disabled segments: 10% opacity (255 * 0.10)
        image_data.data[offset + 3] = 65;
      } else {
        // Enabled segments: 100% opacity
        image_data.data[offset + 3] = 255;
      }
    }

    // 7. Draw the modified image frame data back onto the canvas screen
    ctx.putImageData(image_data, 0, 0);
  }, [segments_disabled, field.hash_sha256_content, field.asset.segments_map, canvas_image]);

  /*
   * HTML
   */

  if (!field.image__is_initialized) return null;

  return (
    <>
      <div className="absolute bg-black inset-0 pointer-events-none size-full" />
      <canvas ref={ref_canvas} className="absolute inset-0 pointer-events-none size-full image-render-pixelated" />
    </>
  );
}
